import { defineStore } from "pinia";
import { useEmployeeStore } from "../stores/employees";
import { addMonthsToDateString } from "@/helpers/dateUtils";

// GraphQL
import { provideApolloClient } from "@vue/apollo-composable";
import apolloClient from "../graphql/client";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";

provideApolloClient(apolloClient);

// Remove old V1 mutation - only V2 is used
const { mutate: updateProfileRequestV2 } = useMutation(
  gql`
    mutation updateHrProfile($updateProfileId: ID!, $data: ProfileDataInputV2) {
      updateHrProfileV2(id: $updateProfileId, data: $data) {
        message
        error
      }
    }
  `
);

/**
 * Creates an empty profile scaffolding based on the HR profile schema
 * @param {Array} hrProfileData - The HR profile schema data
 * @returns {Object} Empty profile object with default values for each field
 */
function createEmptyProfileScaffolding(hrProfileData) {
  const emptyScaffolding = {};
  for (const fieldGroup of hrProfileData) {
    for (const field of fieldGroup.fields) {
      if (emptyScaffolding[fieldGroup.group] === undefined) {
        emptyScaffolding[fieldGroup.group] = {};
      }
      if (!emptyScaffolding[fieldGroup.group][field.name]) {
        // Must set default depending on field type from profileSchema
        switch (field.type) {
          case "number":
            emptyScaffolding[fieldGroup.group][field.name] = undefined;
            break;
          case "select":
            if (field.multiple) {
              emptyScaffolding[fieldGroup.group][field.name] = [];
            } else {
              emptyScaffolding[fieldGroup.group][field.name] = "";
            }
            break;
          case "boolean":
            emptyScaffolding[fieldGroup.group][field.name] = false;
            break;
          case "date":
            emptyScaffolding[fieldGroup.group][field.name] = "";
            break;
          case "text":
            emptyScaffolding[fieldGroup.group][field.name] = "";
            break;
          case "country":
            if (field.multiple) {
              emptyScaffolding[fieldGroup.group][field.name] = [];
            } else {
              emptyScaffolding[fieldGroup.group][field.name] = "";
            }
            break;
          case "workSchedule":
            emptyScaffolding[fieldGroup.group][field.name] = [];
            break;
          default:
            // Default to empty string
            // This is the default for all other types
            // (e.g. "text", "select", etc.)
            emptyScaffolding[fieldGroup.group][field.name] = "-";
        }
      }
    }
  }
  return emptyScaffolding;
}

export const useHrProfileStore = defineStore("profile", {
  state: () => ({
    profile: {},  // Remove useStorage - always load fresh from backend
    profileInitial: {},
    profileDataHistory: [],
    profileDataHistoryIndex: -1,
    kid: undefined,
    loading: false,
    saving: false,
    hrProfileSchema: [], // Store schema for use in getters
  }),
  getters: {
    didProfileChange() {
      return JSON.stringify(this.profile) !== JSON.stringify(this.profileInitial);
    },
    isProfileEmpty() {
      // Check if profile is truly empty by comparing to empty scaffolding
      if (Object.keys(this.profile).length === 0) {
        return true;
      }
      // If schema is available, compare against empty scaffolding
      if (this.hrProfileSchema.length > 0) {
        const emptyScaffolding = createEmptyProfileScaffolding(this.hrProfileSchema);
        return JSON.stringify(this.profile) === JSON.stringify(emptyScaffolding);
      }
      return Object.keys(this.profile).length === 0;
    },
    // Remove isProfileComplete - unused, validation is in useProfileValidation composable
    profileHistoryItems() {
      return this.profileDataHistory.length;
    },
    profileHistory() {
      if (this.profileDataHistory.length > 0 && this.profileDataHistoryIndex >= 0) {
        if (this.profileDataHistory[this.profileDataHistoryIndex]) {
          return this.profileDataHistory[this.profileDataHistoryIndex];
        }
      }
      return undefined;
    },
    profileCanNavigateNext() {
      return this.profileDataHistoryIndex + 1 <= this.profileDataHistory.length - 1;
    },
    profileCanNavigatePrevious() {
      return this.profileDataHistoryIndex > -1 && this.profileDataHistory.length > 0;
    },
  },
  actions: {
    async getProfile(hrProfileData) {
      // console.log('getProfile', this.loading);
      if (this.loading) return { code: "ERROR", message: "Profile is already loading" };

      this.resetData();

      this.loading = true;
      
      // Store schema for use in getters
      this.hrProfileSchema = hrProfileData;
      
      // Create empty scaffolding using helper function
      const emptyScaffolding = createEmptyProfileScaffolding(hrProfileData);

      // Reset directly (makes UI changes look faster)
      this.profile = { ...emptyScaffolding };
      this.profileInitial = JSON.parse(JSON.stringify(this.profile)); //{ ...this.profile };
      this.kid = null;

      //console.log(emptyScaffolding);

      const employeeStore = useEmployeeStore();

      if (!employeeStore.selectedEmployee?.id) {
        this.loading = false;
        this.profile = {};
        this.kid = null;
        return { code: "ERROR", message: "noEmployeeSelected" };
      }

      try {
        // use apollo client directly
        const result = await apolloClient.query({
          query: gql`
            query encryptedProfileData($employeeId: ID) {
              companyEmployee(where: { id: $employeeId }) {
                profileData
                profileHistory
              }
            }
          `,
          variables: { employeeId: employeeStore.selectedEmployee.id },
          fetchPolicy: "no-cache",
        });
        //console.dir(result);
        if (result.data?.companyEmployee) {
          if (result.data.companyEmployee.profileData) {
            delete result.data.companyEmployee.profileData.error;

            // this.profile = {
            // 	...emptyScaffolding,
            // 	...result.data.companyEmployee.profileData,
            // };
            this.profile = this.normalizeProfileData(hrProfileData, {
              ...emptyScaffolding,
              ...result.data.companyEmployee.profileData,
            });
            this.profileInitial = JSON.parse(JSON.stringify(this.profile));
            this.kid = employeeStore.selectedEmployee.id;

            if (result.data.companyEmployee.profileHistory) {
              this.profileDataHistory = result.data.companyEmployee.profileHistory;
            }
            this.loading = false;
            return { code: "SUCCESS", message: "Load complete" };
          }
          this.loading = false;
          return { code: "ERROR", message: "No profile found" };
        }
        console.dir(result);
        this.loading = false;
        return { code: "ERROR", message: "Error while loading user profile" };
      } catch (e) {
        console.dir(e);
        this.loading = false;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    /**
     * Load profile for a specific employee ID (used for read-only views)
     * @param {string} employeeId - The employee ID to load profile for
     * @param {Array} hrProfileData - The HR profile schema data
     * @returns {Object} Result with code and message
     */
    async getProfileForEmployee(employeeId, hrProfileData) {
      if (this.loading) return { code: "ERROR", message: "Profile is already loading" };

      if (!employeeId) {
        return { code: "ERROR", message: "noEmployeeIdProvided" };
      }

      this.resetData();
      this.loading = true;
      
      // Store schema for use in getters
      this.hrProfileSchema = hrProfileData;
      
      // Create empty scaffolding using helper function
      const emptyScaffolding = createEmptyProfileScaffolding(hrProfileData);

      // Reset directly (makes UI changes look faster)
      this.profile = { ...emptyScaffolding };
      this.profileInitial = JSON.parse(JSON.stringify(this.profile));
      this.kid = null;

      try {
        // use apollo client directly
        const result = await apolloClient.query({
          query: gql`
            query encryptedProfileData($employeeId: ID) {
              companyEmployee(where: { id: $employeeId }) {
                profileData
                profileHistory
              }
            }
          `,
          variables: { employeeId },
          fetchPolicy: "no-cache",
        });
        
        if (result.data?.companyEmployee) {
          if (result.data.companyEmployee.profileData) {
            delete result.data.companyEmployee.profileData.error;

            this.profile = this.normalizeProfileData(hrProfileData, {
              ...emptyScaffolding,
              ...result.data.companyEmployee.profileData,
            });
            this.profileInitial = JSON.parse(JSON.stringify(this.profile));
            this.kid = employeeId;

            if (result.data.companyEmployee.profileHistory) {
              this.profileDataHistory = result.data.companyEmployee.profileHistory;
            }
            this.loading = false;
            return { code: "SUCCESS", message: "Load complete" };
          }
          this.loading = false;
          return { code: "ERROR", message: "No profile found" };
        }
        console.dir(result);
        this.loading = false;
        return { code: "ERROR", message: "Error while loading user profile" };
      } catch (e) {
        console.dir(e);
        this.loading = false;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    normalizeProfileData(schema, data) {
      const normalized = {};
      for (const group of schema) {
        const groupName = group.group;
        normalized[groupName] = {};
        for (const field of group.fields) {
          const value = data?.[groupName]?.[field.name];
          let useDefault = false;

          switch (field.type) {
            case "number":
              if (value === undefined || value === "" || Number.isNaN(Number(value))) {
                useDefault = true;
              }
              normalized[groupName][field.name] = useDefault ? field.defaultValue ?? field.defaultValue : Number(value);
              break;
            case "select":
              if (field.multiple) {
                if (Array.isArray(value)) {
                  normalized[groupName][field.name] = value.map(String);
                } else if (typeof value === "string" && value !== "") {
                  normalized[groupName][field.name] = value.split(",").map((v) => v.trim());
                } else if (value === undefined || value === null) {
                  normalized[groupName][field.name] = field.defaultValue ?? [];
                } else {
                  normalized[groupName][field.name] = [];
                }
              } else {
                // If options are defined, check if value is valid
                const validOptions = field.options?.map((opt) => opt.value) ?? [];
                if (value === undefined || value === null || (validOptions.length && !validOptions.includes(value))) {
                  normalized[groupName][field.name] = field.defaultValue ?? "";
                } else {
                  normalized[groupName][field.name] = String(value);
                }
              }
              break;
            case "date":
              if (value === undefined || value === null || value === "") {
                normalized[groupName][field.name] = field.defaultValue ?? "";
              } else {
                normalized[groupName][field.name] = value;
              }
              break;
            case "country":
              if (field.multiple) {
                if (Array.isArray(value)) {
                  // Already in Profile V2 array format
                  normalized[groupName][field.name] = value.map(String);
                } else if (typeof value === "string" && value !== "") {
                  // Convert legacy Profile V1 nationality string to Profile V2 array format
                  // Handles single values like "LU" or comma-separated like "LU,FR"
                  normalized[groupName][field.name] = value.split(",").map((v) => v.trim()).filter(v => v.length > 0);
                } else if (value === undefined || value === null) {
                  normalized[groupName][field.name] = field.defaultValue ?? [];
                } else {
                  // Handle any other type by converting to array format for Profile V2
                  normalized[groupName][field.name] = [String(value)];
                }
              } else {
                // Single country field (not multiple)
                if (value === undefined || value === null || value === "") {
                  normalized[groupName][field.name] = field.defaultValue ?? "";
                } else {
                  normalized[groupName][field.name] = String(value);
                }
              }
              break;
            default:
              if (value === undefined || value === null) {
                normalized[groupName][field.name] = field.defaultValue ?? "";
              } else {
                normalized[groupName][field.name] = value;
              }
          }
        }
      }
      return normalized;
    },
    // Remove saveChange() - redundant wrapper
    // Direct calls should use saveChangesV2()
    async saveChangesV2() {
      this.saving = true;
      try {
        const result = await updateProfileRequestV2({
          updateProfileId: this.kid,
          data: this.profile,
        });
        //console.dir(result);
        this.saving = false;
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        if (result.data.updateHrProfileV2.error) {
          return {
            code: "ERROR",
            message: result.data.updateHrProfileV2.message,
          };
        }
        
        // Update profileInitial after successful save
        this.profileInitial = JSON.parse(JSON.stringify(this.profile));
        
        return result;
      } catch (e) {
        //console.dir(e);
        this.saving = false;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async historyNext() {
      /* move index up to navigate back in history */
      if (this.profileDataHistoryIndex < this.profileDataHistory.length - 1) {
        this.profileDataHistoryIndex++;
      }
    },
    async historyPrevious() {
      /* move index to latest history item on Zero, -1 to disable history */
      if (this.profileDataHistoryIndex > -1) {
        this.profileDataHistoryIndex--;
      }
    },
    resetData() {
      this.profile = {};
      this.profileInitial = {};
      this.kid = undefined;
      this.loading = false;
      this.saving = false;
      this.profileDataHistoryIndex = -1;
      this.hrProfileSchema = [];
    },
    /**
     * Calculate and set trial date based on startDate and trialDateOffsetOption
     * Called when either of these fields change (via store subscription)
     */
    calculateAndSetTrialDate() {
      if (this.isProfileEmpty || !this.profile?.contract) return;
      
      const contract = this.profile.contract;
      const startDate = contract.startDate;
      const offsetOption = contract.trialDateOffsetOption;
      
      // Only auto-calculate if offset option is "3", "6", or "12" (not "manual") and we have a startDate
      if (offsetOption && offsetOption !== 'manual' && startDate) {
        const months = parseInt(offsetOption);
        if (!isNaN(months) && months > 0) {
          const calculated = addMonthsToDateString(startDate, months);
          // Only update if the calculated value is different to avoid infinite loops
          if (calculated && contract.trialDate !== calculated) {
            contract.trialDate = calculated;
          }
        }
      }
    },
  },
});
